import { Box, InputBase, MenuItem, Select, Tooltip } from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { InfoOutlined } from '@mui/icons-material';
import { PaloAmount } from '@synergy/components-palo';
import { ReactElement, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

/** From here on the month counts as nearly used up (#2283; the backend filter uses the same). */
export const NEAR_LIMIT_RATIO = 0.9;

const UNLIMITED = -1;

interface LimitCellProps {
    value?: number | null;
    /** The monthly limit is chosen from the agreed steps; the limit per order is any amount. */
    choices?: number[];
    /** Said beside the amount for as long as it holds, e.g. that it is cut to the month (R-173). */
    hint?: string;
    onSave: (value: number | null) => void;
}

/**
 * A limit that is changed where it stands (#2283). Empty is unlimited, as in
 * the person's form.
 */
export const PaloLimitCell = ({ value, choices, hint, onSave }: LimitCellProps): ReactElement => {
    const intl = useIntl();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');

    const shown = null === value || undefined === value ? '–' : <PaloAmount value={value} />;

    if (!editing) {
        return (
            <Box
                component="span"
                role="button"
                title={intl.formatMessage({ id: 'app.contacts.limits.edit' })}
                onClick={event => {
                    // The row opens the person; this only edits the limit.
                    event.stopPropagation();
                    setDraft(null === value || undefined === value ? '' : String(value));
                    setEditing(true);
                }}
                sx={{
                    cursor: 'pointer',
                    padding: '2px 6px',
                    margin: '-2px -6px',
                    borderRadius: `${tokens.radius.small}px`,
                    '&:hover': { background: c.accentSoft },
                }}
            >
                {shown}
                {hint && (
                    <Tooltip title={hint} placement="top">
                        <InfoOutlined
                            aria-label={hint}
                            sx={{ fontSize: 14, color: c.ink3, marginLeft: '4px', verticalAlign: '-2px' }}
                        />
                    </Tooltip>
                )}
            </Box>
        );
    }

    const save = (raw: string | number): void => {
        setEditing(false);
        const next = '' === raw || UNLIMITED === Number(raw) ? null : Number(raw);

        if (next !== (value ?? null) && (null === next || !Number.isNaN(next))) {
            onSave(next);
        }
    };

    return (
        <Box component="span" onClick={event => event.stopPropagation()}>
            {choices ? (
                <Select
                    autoFocus
                    open
                    size="small"
                    variant="standard"
                    value={'' === draft ? UNLIMITED : Number(draft)}
                    onChange={event => save(Number(event.target.value))}
                    onClose={() => setEditing(false)}
                    sx={{ fontSize: '12.5px', minWidth: 110 }}
                >
                    <MenuItem value={UNLIMITED}>
                        <FormattedMessage id="app.contacts.limits.unlimited" />
                    </MenuItem>
                    {choices.map(choice => (
                        <MenuItem key={choice} value={choice}>
                            <FormattedNumber value={choice} minimumFractionDigits={2} />
                        </MenuItem>
                    ))}
                </Select>
            ) : (
                <InputBase
                    autoFocus
                    type="number"
                    value={draft}
                    placeholder={intl.formatMessage({ id: 'app.contacts.limits.unlimited' })}
                    onChange={event => setDraft(event.target.value)}
                    onBlur={() => save(draft)}
                    onKeyDown={event => {
                        if ('Enter' === event.key) {
                            save(draft);
                        }
                        if ('Escape' === event.key) {
                            setEditing(false);
                        }
                    }}
                    inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    sx={{
                        width: 110,
                        fontSize: '12.5px',
                        padding: '0 6px',
                        border: `1px solid ${c.accent}`,
                        borderRadius: `${tokens.radius.small}px`,
                        background: c.surface,
                    }}
                />
            )}
        </Box>
    );
};

interface UsageCellProps {
    used?: number | null;
    monthlyLimit?: number | null;
}

/** What a person used of their month, with a bar and what is left (#2283). */
export const PaloUsageCell = ({ used, monthlyLimit }: UsageCellProps): ReactElement => {
    if (0 === monthlyLimit) {
        return (
            <Box component="span" sx={{ color: c.ink3 }}>
                <FormattedMessage id="app.contacts.limits.approvalAlways" />
            </Box>
        );
    }

    const spent = used ?? 0;
    const hasLimit = null !== monthlyLimit && undefined !== monthlyLimit;
    const share = hasLimit ? Math.min(1, spent / (monthlyLimit as number)) : 0;
    const near = hasLimit && share >= NEAR_LIMIT_RATIO;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {hasLimit && (
                <Box sx={{ width: 72, height: 5, borderRadius: 3, background: c.neutralSoft, flexShrink: 0 }}>
                    <Box
                        sx={{
                            width: `${share * 100}%`,
                            height: '100%',
                            borderRadius: 3,
                            background: near ? c.warn : c.accent,
                        }}
                    />
                </Box>
            )}
            <Box component="span" sx={{ fontWeight: near ? 650 : undefined, color: near ? c.warnInk : c.ink }}>
                <PaloAmount value={spent} />
            </Box>
            {hasLimit && (
                <Box component="span" sx={{ color: c.ink3 }}>
                    <FormattedMessage
                        id="app.contacts.limits.rest"
                        values={{
                            amount: (
                                <FormattedNumber
                                    value={Math.max(0, (monthlyLimit as number) - spent)}
                                    maximumFractionDigits={0}
                                />
                            ),
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};
